import React, { useState, useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableHeadSortable,
  TableCell,
} from '@/design-system/table';
import { Checkbox } from '@/design-system/checkbox';
import { Skeleton } from '@/design-system/skeleton';
import {
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
} from '@/design-system/empty-state';
import { cn } from '@/design-system/lib/utils';
import { FeedbackStatus } from '../Feedback';
import { TableDataProps } from '@/types/components';
import { SortDirection, Column } from '@/types/common';

export const TableData = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  sorting,
  selection,
  rowActions,
  emptyState,
  className = '',
  size = 'medium',
  onRowClick,
}: TableDataProps<T>) => {
  const [internalSorting, setInternalSorting] = useState<{
    field: string;
    direction: SortDirection;
  }>({
    field: sorting?.field || '',
    direction: sorting?.direction || null,
  });

  // Size variants
  const sizeClasses = {
    small: 'text-xs',
    medium: 'text-[14px]',
    large: 'text-base',
  };

  const cellPadding = {
    small: 'px-3 py-2',
    medium: 'px-6 py-4',
    large: 'px-8 py-6',
  };

  // Handle sorting — cycle asc → desc → null.
  const handleSort = (field: string) => {
    const column = columns.find((col) => col.key === field);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';

    if (internalSorting.field === field) {
      if (internalSorting.direction === 'asc') {
        newDirection = 'desc';
      } else if (internalSorting.direction === 'desc') {
        newDirection = null;
      }
    }

    const newSorting = { field, direction: newDirection };
    setInternalSorting(newSorting);
    sorting?.onSort?.(field, newDirection);
  };

  // Sort data if no external sorting
  const sortedData = useMemo(() => {
    if (sorting?.onSort || !internalSorting.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = a[internalSorting.field];
      const bValue = b[internalSorting.field];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return internalSorting.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, internalSorting, sorting?.onSort]);

  // Handle selection
  // Selectable rows respect `isRowSelectable` so select-all + the
  // "fully selected" header state only count rows that can actually be
  // checked. Disabled rows (e.g. wrong selection group) are skipped.
  const selectableRows = useMemo(() => {
    if (!selection?.isRowSelectable) return sortedData;
    return sortedData.filter(selection.isRowSelectable);
  }, [sortedData, selection]);

  const handleSelectAll = (checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    if (checked) {
      const allKeys = selectableRows.map(selection.getRowKey);
      selection.onSelectionChange(allKeys);
    } else {
      selection.onSelectionChange([]);
    }
  };

  const handleSelectRow = (record: T, checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    const key = selection.getRowKey(record);
    const newSelectedKeys = checked
      ? [...selection.selectedKeys, key]
      : selection.selectedKeys.filter((k) => k !== key);

    selection.onSelectionChange(newSelectedKeys);
  };

  // Render cell content
  const renderCell = (column: Column<T>, record: T, index: number) => {
    const value = record[column.key];

    if (column.render) {
      return column.render(value, record, index);
    }

    // Auto-render status badges
    if (
      column.key.toLowerCase().includes('status') &&
      typeof value === 'string'
    ) {
      return <FeedbackStatus status={value} />;
    }

    return value;
  };

  // Sticky treatment per header cell. The `actions` column pins to the right
  // with an opaque fill + the legacy `.shadow-left` edge (still in index.css).
  const headStickyClass = (column: Column<T>) =>
    column.key === 'actions'
      ? 'sticky right-0 z-10 bg-bg-weak-50 shadow-left'
      : column.sticky === 'right'
        ? 'sticky right-0 z-10 border-l border-stroke-soft-200 bg-bg-weak-50'
        : column.sticky === 'left'
          ? 'sticky left-0 z-10 border-r border-stroke-soft-200 bg-bg-weak-50'
          : '';

  const alignClass = (align?: Column<T>['align']) =>
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : '';

  // Loading state — kit Skeleton header block + rows.
  if (loading) {
    return (
      <div
        className={cn(
          'overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-white-0',
          className
        )}
      >
        <Skeleton className="h-12 rounded-none" />
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="flex h-16 items-center border-t border-stroke-soft-200 px-6"
          >
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!data.length) {
    // Auto-detect search scenario by checking URL params or document title
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam =
      urlParams.get('search') || urlParams.get('query') || urlParams.get('q');

    // Also check for search input in the document
    const searchInput = document.querySelector(
      'input[placeholder*="Search"], input[placeholder*="search"], input[type="search"]'
    ) as HTMLInputElement;
    const searchTerm = searchParam || searchInput?.value || '';

    return (
      <div
        className={cn(
          'rounded-lg border border-stroke-soft-200 bg-bg-white-0',
          className
        )}
      >
        {emptyState ? (
          <div className="p-12 text-center">{emptyState}</div>
        ) : (
          <EmptyState size="md" className="p-12">
            <div className="size-[148px]">
              <img
                src="/images/no-results.png"
                alt="No results found"
                className="h-full w-full"
              />
            </div>
            <EmptyStateTitle>
              {searchTerm ? `No results found for "${searchTerm}"` : 'No data'}
            </EmptyStateTitle>
            <EmptyStateDescription>
              Try checking the spelling or using a different search term.
            </EmptyStateDescription>
          </EmptyState>
        )}
      </div>
    );
  }

  const allSelected =
    !!selection &&
    selectableRows.length > 0 &&
    selection.selectedKeys.length === selectableRows.length;
  const someSelected =
    !!selection && selection.selectedKeys.length > 0 && !allSelected;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-white-0',
        className
      )}
    >
      <Table containerClassName="overflow-x-auto">
        <TableHeader>
          <TableRow>
            {/* Selection column */}
            {selection && (
              <TableHead className={cn(cellPadding[size], 'w-px')}>
                <Checkbox
                  aria-label="Select all rows"
                  checked={
                    allSelected ? true : someSelected ? 'indeterminate' : false
                  }
                  disabled={
                    selection.headerCheckboxDisabled ||
                    selectableRows.length === 0
                  }
                  onCheckedChange={(checked) =>
                    handleSelectAll(checked === true)
                  }
                />
              </TableHead>
            )}

            {/* Data columns */}
            {columns.map((column) =>
              column.sortable ? (
                <TableHeadSortable
                  key={column.key}
                  label={column.title}
                  direction={
                    internalSorting.field === column.key
                      ? internalSorting.direction
                      : null
                  }
                  onSort={() => handleSort(column.key)}
                  className={cn(
                    cellPadding[size],
                    'whitespace-nowrap',
                    alignClass(column.align),
                    headStickyClass(column)
                  )}
                  style={{ width: column.width }}
                />
              ) : (
                <TableHead
                  key={column.key}
                  className={cn(
                    cellPadding[size],
                    'whitespace-nowrap',
                    alignClass(column.align),
                    headStickyClass(column)
                  )}
                  style={{ width: column.width }}
                >
                  {column.title}
                </TableHead>
              )
            )}

            {/* Actions column */}
            {rowActions && (
              <TableHead
                className={cn(
                  cellPadding[size],
                  'sticky right-0 z-10 border-l border-stroke-soft-200 bg-bg-weak-50'
                )}
              >
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>

        <TableBody>
          {sortedData.map((record, index) => {
            const rowKey = selection?.getRowKey?.(record) || index.toString();
            const isSelected =
              selection?.selectedKeys.includes(rowKey) || false;

            return (
              <TableRow
                key={rowKey}
                data-state={isSelected ? 'selected' : undefined}
                className={cn(
                  'group align-middle',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(record, index)}
              >
                {/* Selection column */}
                {selection && (
                  <TableCell
                    className={cellPadding[size]}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      aria-label="Select row"
                      checked={isSelected}
                      disabled={
                        selection.isRowSelectable
                          ? !selection.isRowSelectable(record)
                          : false
                      }
                      onCheckedChange={(checked) =>
                        handleSelectRow(record, checked === true)
                      }
                    />
                  </TableCell>
                )}

                {/* Data columns */}
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      cellPadding[size],
                      sizeClasses[size],
                      'whitespace-nowrap',
                      alignClass(column.align),
                      column.key === 'actions' &&
                        'sticky right-0 z-[2] bg-bg-white-0 shadow-left'
                    )}
                  >
                    {renderCell(column, record, index)}
                  </TableCell>
                ))}

                {/* Actions column */}
                {rowActions && (
                  <TableCell
                    className={cn(
                      cellPadding[size],
                      sizeClasses[size],
                      'sticky right-0 z-10 border-l border-stroke-soft-200 bg-bg-white-0 text-right font-medium'
                    )}
                  >
                    {rowActions.render(record, index)}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default TableData;
